export const formatShortDateTime = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const FIELD_LABELS = {
  client_name: 'Client name',
  display_as: 'Display as',
  phone: 'Phone',
  email: 'Email',
  city: 'City',
  state: 'State',
  zip_code: 'Zip code',
  client_address: 'Address',
  address_2: 'Address line 2',
  latitude: 'Latitude',
  longitude: 'Longitude',
  client_status: 'Status',
  invoice_pattern: 'Invoice pattern',
  name: 'Name',
  title: 'Title',
  is_primary: 'Primary',
  notes: 'Notes',
};

const formatStatus = (value) => {
  if (value === 1 || value === '1') return 'Active';
  if (value === 0 || value === '0') return 'Inactive';
  return String(value);
};

const formatBoolean = (value) => (
  value === true || value === 'true' ? 'Yes' : value === false || value === 'false' ? 'No' : String(value)
);

const formatFieldValue = (field, value) => {
  if (value === null || value === undefined || value === '') return 'empty';
  if (field === 'client_status') return formatStatus(value);
  if (field === 'is_primary') return formatBoolean(value);
  if (typeof value === 'boolean') return formatBoolean(value);
  return String(value);
};

const formatChanges = (changes = []) => {
  if (!Array.isArray(changes) || changes.length === 0) return null;
  return changes
    .map((change) => {
      const field = FIELD_LABELS[change.field] || change.field;
      const fromValue = formatFieldValue(change.field, change.from);
      const toValue = formatFieldValue(change.field, change.to);
      return `${field}: ${fromValue} -> ${toValue}`;
    })
    .join('\n');
};

const formatAdminEntry = (entry) => {
  if (!entry) return null;
  if (typeof entry === 'string') return entry;
  if (entry.name && entry.email) return `${entry.name} (${entry.email})`;
  if (entry.name) return entry.name;
  if (entry.email) return entry.email;
  if (entry.id) return entry.id;
  return String(entry);
};

const formatAdminList = (entries = []) => {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  return entries.map(formatAdminEntry).filter(Boolean).join(', ');
};

const ACTION_MESSAGES = {
  'client.created': 'Client created',
  'client.updated': 'Client updated',
  'client.status_updated': 'Status updated',
  'client.invoice_pattern_updated': 'Invoice pattern updated',
  'client.admins_updated': 'Admins updated',
  'client.request_accepted': 'Client request accepted',
  'poc.created': 'POC created',
  'poc.updated': 'POC updated',
  'poc.deleted': 'POC deleted',
};

export const formatClientLogEntry = (log) => {
  if (!log) return '';
  const base = ACTION_MESSAGES[log.action] || log.action || 'Activity';
  const changesText = formatChanges(log.metadata?.changes);
  if (changesText) {
    const label = log.metadata?.label ? `${base} (${log.metadata.label})` : base;
    return `${label}\n${changesText}`;
  }

  if (log.action === 'client.admins_updated') {
    const parts = [];
    const added = formatAdminList(log.metadata?.added);
    const removed = formatAdminList(log.metadata?.removed);
    if (added) parts.push(`Added: ${added}`);
    if (removed) parts.push(`Removed: ${removed}`);
    if (parts.length) return `${base}\n${parts.join('\n')}`;
  }

  if (
    log.action === 'client.created'
    || log.action === 'client.request_accepted'
    || log.action === 'poc.created'
    || log.action === 'poc.deleted'
  ) {
    const parts = [];
    if (log.metadata?.label) parts.push(`Name: ${log.metadata.label}`);
    if (log.metadata?.client_name) parts.push(`Client name: ${log.metadata.client_name}`);
    if (log.metadata?.display_as && log.metadata.display_as !== log.metadata.client_name) {
      parts.push(`Display as: ${log.metadata.display_as}`);
    }
    if (log.metadata?.name && !log.metadata?.label) parts.push(`Name: ${log.metadata.name}`);
    if (log.metadata?.invoice_pattern) parts.push(`Invoice pattern: ${log.metadata.invoice_pattern}`);
    if (log.metadata?.phone) parts.push(`Phone: ${log.metadata.phone}`);
    if (log.metadata?.email) parts.push(`Email: ${log.metadata.email}`);
    if (log.action === 'client.request_accepted' && log.metadata?.request_notes) {
      parts.push(`Request notes: ${log.metadata.request_notes}`);
    }
    const admins = formatAdminList(log.metadata?.admins);
    if (admins) parts.push(`Admins: ${admins}`);
    if (parts.length) return `${base}\n${parts.join('\n')}`;
  }

  return base;
};
